import { useContext, type ReactNode } from 'react';
import { TabGroupContext } from './TabGroup.js';

type TabBadgeTone = 'default' | 'danger' | 'warning' | 'info' | 'success';

interface TabButtonProps {
  /** 現在選択中かどうか */
  active: boolean;
  /** クリック時のハンドラ */
  onClick: () => void;
  /** ラベル */
  label: ReactNode;
  /** 件数バッジ（任意） */
  count?: number;
  /** バッジの色トーン（active 時の背景色） */
  tone?: TabBadgeTone;
  /** disabled */
  disabled?: boolean;
  /**
   * active 時のボタン自身の背景・影の描画有無を上書きする。
   * 未指定なら親 <TabGroup> の sliding（既定 ON）に従い、sliding 時は背景を描画せず
   * TabGroup のスライドピルを背後から見せる。通常は指定不要。
   */
  transparentActive?: boolean;
}

/** @graph-connects none */
const toneColors: Record<TabBadgeTone, string> = {
  default: 'var(--color-accent-primary)',
  danger: 'var(--color-error)',
  warning: 'var(--color-warning)',
  info: 'var(--color-info)',
  success: 'var(--color-success)',
};

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business タブ風のセグメントボタン。active時は浮き上がる背景+影、optional な件数バッジ付き。<TabGroup>と組み合わせて使用し、親の shape='pill' のときは完全な丸になる
 * @graph-connects none
 */
export function TabButton({
  active,
  onClick,
  label,
  count,
  tone = 'default',
  disabled = false,
  transparentActive,
}: TabButtonProps) {
  // 親 <TabGroup> が sliding（既定 ON）のときは、各ボタンのアクティブ背景を描画せず
  // TabGroup のスライドピルを背後から見せる。明示的な transparentActive はそれを上書きする。
  const { sliding, shape } = useContext(TabGroupContext);
  const isTransparentActive = transparentActive ?? sliding;
  // 親が pill のときはボタン側も完全な丸にして、スライドピルと輪郭を一致させる
  const isPill = shape === 'pill';
  const activeClass = isTransparentActive
    ? 'text-[var(--color-text-primary)]'
    : 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] shadow-sm';
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      disabled={disabled}
      className={`
        relative z-10 text-sm font-medium transition-colors
        ${isPill ? 'px-3.5 py-1.5 rounded-full' : 'px-4 py-2 rounded-md'}
        disabled:opacity-50 disabled:cursor-not-allowed
        ${active ? activeClass : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'}
      `}
    >
      <span>{label}</span>
      {typeof count === 'number' && (
        <span
          className="ml-2 inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-xs font-semibold"
          style={{
            backgroundColor: active ? toneColors[tone] : 'var(--color-bg-base)',
            color: active ? '#fff' : 'var(--color-text-muted)',
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}
